<template>
  <table class="table align-items-center mb-0">
    <thead>
      <tr>
        <th class="text-sm">Port</th>
        <th class="text-sm">Port Name</th>
        <th class="text-sm">Status</th>
        <th class="text-sm">Speed</th>
        <th class="text-sm">Duplex</th>
        <th class="text-sm">TX Power (dBm)</th>
        <th class="text-sm">RX Power (dBm)</th>
        <th class="text-sm">Bias Current (mA)</th>
        <th class="text-sm">Supply Voltage (V)</th>
        <th class="text-sm">Temperature (°C)</th>
        <th class="text-sm">Inlet Flow</th>
        <th class="text-sm">Outlet Flow</th>
        <th class="text-sm">Description</th>
        <th class="text-sm">Name</th>
        <th class="text-sm">Serial Number</th>
      </tr>
    </thead>
    <tbody>
      <template v-if="loading">
        <tr>
          <td colspan="15">
            <Skalaton />
          </td>
        </tr>
      </template>
      <template v-else-if="hasMergedData">
        <tr
          v-for="(port, index) in mergedDataArray"
          :key="index"
        >
          <td class="text-sm font-weight-bold">{{ port.portNum || port.port_num || 'N/A' }}</td>
          <td class="text-sm">{{ port.portName || port.port_name || 'N/A' }}</td>
          <td class="text-sm">
            <span
              class="badge"
              :class="getStatusClass(port.portStatus)"
              style="text-transform: uppercase; color: rgb(255 249 249) !important;"
            >
              {{ getStatusText(port.portStatus) }}
            </span>
          </td>
          <td class="text-sm">{{ formatSpeed(port.portSpeed) }}</td>
          <td class="text-sm">{{ formatDuplex(port.portDuplex) }}</td>
          <td class="text-sm" :class="getTxPowerClass(port.txPower)">
            {{ formatPower(port.txPower) }}
          </td>
          <td class="text-sm" :class="getRxPowerClass(port.rxPower)">
            {{ formatPower(port.rxPower) }}
          </td>
          <td class="text-sm">{{ formatCurrent(port.txBiasCur) }}</td>
          <td class="text-sm">{{ formatVoltage(port.supVol) }}</td>
          <td class="text-sm" :class="getTemperatureClass(port.temperature)">
            {{ formatTemperature(port.temperature) }}
          </td>
          <td class="text-sm">{{ port.inletFlow || 'N/A' }}</td>
          <td class="text-sm">{{ port.outletFlow || 'N/A' }}</td>
          <td class="text-sm">{{ port.desc || 'N/A' }}</td>
          <td class="text-sm">{{ port.name || 'N/A' }}</td>
          <td class="text-sm">{{ port.sn || 'N/A' }}</td>
        </tr>
      </template>
      <template v-else>
        <tr>
          <td colspan="15" class="text-center">
            <Skalaton />
            <!-- No merged data available -->
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script>
import Skalaton from '@/components/Skalaton.vue';

export default {
  name: 'TbsEponMergedTable',
  components: {
    Skalaton
  },
  props: {
    ponInfo: {
      type: [Array, Object],
      default: () => []
    },
    basicInfo: {
      type: [Array, Object],
      default: () => []
    },
    loading: Boolean
  },
  computed: {
    hasMergedData() {
      return this.mergedDataArray.length > 0;
    },
    mergedDataArray() {
      // Extract PON data
      let ponData = [];
      if (Array.isArray(this.ponInfo)) {
        ponData = this.ponInfo;
      } else if (this.ponInfo && typeof this.ponInfo === 'object') {
        if (this.ponInfo.optical_ports && Array.isArray(this.ponInfo.optical_ports)) {
          ponData = this.ponInfo.optical_ports;
        } else if (this.ponInfo.ports && Array.isArray(this.ponInfo.ports)) {
          ponData = this.ponInfo.ports;
        } else if (this.ponInfo.data && Array.isArray(this.ponInfo.data)) {
          ponData = this.ponInfo.data;
        }
      }

      // Extract Basic Info data
      let basicData = [];
      if (Array.isArray(this.basicInfo)) {
        basicData = this.basicInfo;
      } else if (this.basicInfo && typeof this.basicInfo === 'object') {
        if (this.basicInfo.ports && Array.isArray(this.basicInfo.ports)) {
          basicData = this.basicInfo.ports;
        } else if (this.basicInfo.data && Array.isArray(this.basicInfo.data)) {
          basicData = this.basicInfo.data;
        }
      }

      // Create a map of basic info by portNum or portName
      const basicInfoMap = new Map();
      basicData.forEach(port => {
        const key = port.portNum || port.port_num || port.portName || port.port_name;
        if (key) {
          basicInfoMap.set(String(key), port);
        }
      });

      // Merge PON data with Basic Info data
      const merged = ponData.map(ponPort => {
        const key = ponPort.portNum || ponPort.port_num || ponPort.portName || ponPort.port_name;
        const basicPort = key ? basicInfoMap.get(String(key)) : null;
        
        if (basicPort) {
          // Merge both data sources
          return {
            ...basicPort,
            ...ponPort,
            // Prefer PON data for portNum/portName if both exist
            portNum: ponPort.portNum || ponPort.port_num || basicPort.portNum || basicPort.port_num,
            portName: ponPort.portName || ponPort.port_name || basicPort.portName || basicPort.port_name,
          };
        }
        return ponPort;
      });

      // Add any basic info ports that don't have PON data
      ponData.forEach(ponPort => {
        const key = ponPort.portNum || ponPort.port_num || ponPort.portName || ponPort.port_name;
        if (key) {
          basicInfoMap.delete(String(key));
        }
      });

      // Add remaining basic info ports
      basicInfoMap.forEach(basicPort => {
        merged.push(basicPort);
      });

      // Sort by portNum if available
      return merged.sort((a, b) => {
        const numA = parseFloat(a.portNum || a.port_num || 0);
        const numB = parseFloat(b.portNum || b.port_num || 0);
        return numA - numB;
      });
    }
  },
  methods: {
    formatPower(power) {
      if (!power || power === '--' || power === 'N/A') return 'N/A';
      const num = parseFloat(power);
      if (isNaN(num)) return power;
      return `${num.toFixed(2)}`;
    },
    formatCurrent(current) {
      if (!current || current === '--' || current === 'N/A') return 'N/A';
      const num = parseFloat(current);
      if (isNaN(num)) return current;
      return `${num.toFixed(2)}`;
    },
    formatVoltage(voltage) {
      if (!voltage || voltage === '--' || voltage === 'N/A') return 'N/A';
      const num = parseFloat(voltage);
      if (isNaN(num)) return voltage;
      return `${num.toFixed(4)}`;
    },
    formatTemperature(temp) {
      if (!temp || temp === '--' || temp === 'N/A') return 'N/A';
      const num = parseFloat(temp);
      if (isNaN(num)) return temp;
      return `${num.toFixed(2)}`;
    },
    formatSpeed(speed) {
      if (!speed) return 'N/A';
      const trimmed = String(speed).trim();
      if (trimmed === '0' || trimmed === '0    ') return 'N/A';
      const num = parseFloat(trimmed);
      if (isNaN(num)) return trimmed;
      if (num >= 10000) return `${num / 1000}G`;
      return `${num}M`;
    },
    formatDuplex(duplex) {
      if (duplex === '1' || duplex === 1) return 'Full';
      if (duplex === '2' || duplex === 2) return 'Full';
      if (duplex === '0' || duplex === 0) return 'Half';
      return 'N/A';
    },
    getStatusClass(status) {
      if (status === '1' || status === 1) return 'bg-success';
      if (status === '2' || status === 2) return 'bg-danger';
      if (status === '0' || status === 0) return 'bg-secondary';
      return 'bg-warning';
    },
    getStatusText(status) {
      if (status === '1' || status === 1) return 'Up';
      if (status === '2' || status === 2) return 'Down';
      if (status === '0' || status === 0) return 'Down';
      return 'Unknown';
    },
    getTxPowerClass(power) {
      if (!power || power === '--' || power === 'N/A') return '';
      const num = parseFloat(power);
      if (isNaN(num)) return '';
      if (num < 0) return 'text-danger';
      if (num < 2) return 'text-warning';
      return 'text-success';
    },
    getRxPowerClass(power) {
      if (!power || power === '--' || power === 'N/A') return '';
      const num = parseFloat(power);
      if (isNaN(num)) return '';
      // RX power is typically negative, more negative is worse
      if (num > -15) return 'text-success';
      if (num > -25) return 'text-warning';
      return 'text-danger';
    },
    getTemperatureClass(temp) {
      if (!temp || temp === '--' || temp === 'N/A') return '';
      const num = parseFloat(temp);
      if (isNaN(num)) return '';
      if (num > 70) return 'text-danger';
      if (num > 60) return 'text-warning';
      return 'text-success';
    }
  }
}
</script>

